import React, { createContext, useContext, useState, useEffect } from "react";
import authService from "../services/authService";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const savedUser = localStorage.getItem("cia_user");
        const savedToken = localStorage.getItem("cia_token");

        if (savedUser && savedToken) {
          // Verify token is still valid by fetching current user
          try {
            const user = await authService.getCurrentUser();
            setUser(user);
          } catch (error) {
            // Token is invalid, clear storage
            console.error("Token validation failed:", error);
            localStorage.removeItem("cia_user");
            localStorage.removeItem("cia_token");
            localStorage.removeItem("cia_refresh_token");
            setUser(null);
          }
        }
      } catch (error) {
        console.error("Error checking auth status:", error);
        localStorage.removeItem("cia_user");
        localStorage.removeItem("cia_token");
        localStorage.removeItem("cia_refresh_token");
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);

      // Call backend API
      const response = await authService.login(email, password);

      if (response.success) {
        const userData = {
          id: response.user.id,
          email: response.user.email,
          firstName: response.user.firstName,
          lastName: response.user.lastName,
          role: response.user.role || "user",
        };

        setUser(userData);
        localStorage.setItem("cia_user", JSON.stringify(userData));
        localStorage.setItem("cia_token", response.tokens.accessToken);
        localStorage.setItem("cia_refresh_token", response.tokens.refreshToken);

        return { success: true };
      } else {
        throw new Error("Login failed");
      }
    } catch (error) {
      const errorMessage = error.message || "Login failed. Please try again.";
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const signup = async (
    firstName,
    lastName,
    email,
    password,
    confirmPassword
  ) => {
    try {
      setLoading(true);
      setError(null);

      // Validate passwords match
      if (password !== confirmPassword) {
        throw new Error("Passwords do not match");
      }

      // Validate password strength
      if (password.length < 8) {
        throw new Error("Password must be at least 8 characters long");
      }

      // Call backend API
      const response = await authService.register(
        email,
        password,
        firstName,
        lastName
      );

      if (response.success) {
        const userData = {
          id: response.user.id,
          email: response.user.email,
          firstName: response.user.firstName,
          lastName: response.user.lastName,
          role: response.user.role || "user",
        };

        setUser(userData);
        localStorage.setItem("cia_user", JSON.stringify(userData));
        localStorage.setItem("cia_token", response.tokens.accessToken);
        localStorage.setItem("cia_refresh_token", response.tokens.refreshToken);

        return { success: true };
      } else {
        throw new Error("Signup failed");
      }
    } catch (error) {
      const errorMessage = error.message || "Signup failed. Please try again.";
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setUser(null);
      setError(null);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value = {
    user,
    loading,
    error,
    login,
    signup,
    logout,
    clearError,
    isAuthenticated: !!user,
    isAdmin: user?.role === "admin",
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
